import { useFlags } from "launchdarkly-react-client-sdk";
import {
  type ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useState
} from "react";
import { FormProvider, SubmitHandler, useForm } from "react-hook-form";

import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import AutoFixHighIcon from "@mui/icons-material/AutoFixHigh";
import CloseIcon from "@mui/icons-material/Close";
import KeyboardAltOutlinedIcon from "@mui/icons-material/KeyboardAltOutlined";
import LoadingButton from "@mui/lab/LoadingButton";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Dialog from "@mui/material/Dialog";
import Divider from "@mui/material/Divider";
import FormControlLabel from "@mui/material/FormControlLabel";
import IconButton from "@mui/material/IconButton";
import Link from "@mui/material/Link";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import Typography from "@mui/material/Typography";
import useTheme from "@mui/material/styles/useTheme";

import { merge } from "lodash";

import CheckboxFormField from "components/checkbox-form-field";
import { SignDocumentIcon } from "components/icons";
import ImageUpload from "components/image-upload";
import SignatureDesk, {
  type SignatureEntryPoint,
  TopazSignatureDesk,
  TypeSignatureDesk,
  useSignatureCanvasMode,
  useSignatureMethod,
  useTopazPadAvailability
} from "components/signature-desk";
import { isSigWebSupportedPlatform } from "components/signature-desk/providers/sigweb.client";
import {
  AI_IMAGE_ENHANCE_FEATURE,
  TOPAZ_SIGNATURE_PAD_FEATURE,
  TYPE_TO_SIGN_FEATURE
} from "constants/feature-flags";
import { useEnhanceSignatureMutation } from "services/users";
import { getEnhanceErrorMessage } from "utils/parse-enhance-image-error.util";

import { validatePhotoDimensions, validatePhotoFileSizes } from "./util";

type SignaturePickerProps = {
  open: boolean;
  isLoading?: boolean;
  hasInitialImage?: boolean;
  currentImageSrc?: string;
  title?: string;
  signatureDeskTitle?: ReactNode;
  submitButtonText?: string;
  submitButtonName?: string;
  onClose: () => void;
  onSubmit?: (files: File[], uploadToEdocs?: boolean) => Promise<void> | void;
  enableHandSignatureOnly?: boolean;
  /**
   * Offers the typed-signature ("Type") mode on this surface when the
   * type-to-sign flag is on. Opt-in per call site; the patient signing
   * surfaces pass it unconditionally, while the staff personal-details
   * signature (SN-19567) derives it from its own flag
   * (TYPE_TO_SIGN_USER_SETTINGS_FEATURE), so that surface needs both flags.
   * Also swaps the first step's helper copy and entry icon to the combined
   * type-or-draw treatment, so the gate keeps every non-opted surface
   * byte-identical to the flag-off render.
   */
  enableTypedSignature?: boolean;
  showUploadToEdocsCheckbox?: boolean;
  /**
   * Which workflow this dialog was opened from. Reported with the
   * `signature_pad_used` RUM action so pad adoption can be read per workflow
   * — "works on the Purchase Agreement, unused on invoices" is the answer the
   * rollout needs, and it is unrecoverable if every surface looks the same.
   */
  entryPoint?: SignatureEntryPoint;
  largeSize?: boolean;
  maxSizeLimit?: number;
  maxWidth?: number;
};

export type SignaturePickerValues = {
  photos: File[];
  uploadToEdocs?: boolean;
};

const DEFAULT_VALUES: SignaturePickerValues = {
  photos: [],
  uploadToEdocs: false
};

export default function SignaturePicker({
  open,
  isLoading,
  title = "Signature",
  signatureDeskTitle = "Signature Desk",
  submitButtonName = "Save",
  submitButtonText,
  hasInitialImage,
  currentImageSrc,
  enableHandSignatureOnly,
  enableTypedSignature,
  showUploadToEdocsCheckbox,
  entryPoint,
  onClose,
  onSubmit,
  largeSize,
  maxSizeLimit = 1 * 1024 * 1024,
  maxWidth = 700
}: SignaturePickerProps) {
  const theme = useTheme();
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [aiError, setAiError] = useState<string | null>(null);
  // "desk" covers BOTH canvas modes: the shared SignatureDesk is the only capture
  // channel (`onCapture: handleDeskCapture`), so a TYPED signature arrives here the
  // same way a drawn one does. Both are already background-free ink, so neither
  // should be offered "Remove Background". Named "desk" rather than "draw" because
  // "draw" reads as excluding the typed mode, which it does not. (SN-18765)
  const [signatureSource, setSignatureSource] = useState<
    "upload" | "desk" | null
  >(null);
  const [step, setStep] = useState<"main" | "signature-desk">(
    enableHandSignatureOnly ? "signature-desk" : "main"
  );
  const [enhanceSignature, { isLoading: isEnhancing }] =
    useEnhanceSignatureMutation();

  useEffect(() => {
    if (enableHandSignatureOnly) {
      setStep("signature-desk");
    }
  }, [enableHandSignatureOnly]);

  // Remembered per workstation — a Topaz pad is attached to one machine, so the
  // choice belongs to the browser, not to the clinic or the user record.
  const { method, setMethod } = useSignatureMethod();

  const flags = useFlags();

  // Kill switch for the AI "Remove Background" action, whose endpoint
  // (POST /ai/signature/enhance/) ships separately from this frontend. Off => the
  // button never mounts, so the endpoint is never called. (SN-18765)
  const isAiEnhanceEnabled = Boolean(flags[AI_IMAGE_ENHANCE_FEATURE]);

  // SigWeb only exists for Windows, so on a Mac the pad can never be reached:
  // no probe (it would only burn the detect timeout on every dialog open) and
  // no entry point (a "Try Again" that cannot succeed is worse than nothing).
  const isTopazEnabled =
    Boolean(flags[TOPAZ_SIGNATURE_PAD_FEATURE]) && isSigWebSupportedPlatform();

  // Probe SigWeb while the dialog is open so a practice that owns a pad gets
  // the tab without being told to click anything. Skipped entirely when the
  // flag is off, so the ~50 practices that will never own a pad make no
  // loopback requests at all.
  const padAvailability = useTopazPadAvailability(isTopazEnabled && open);

  // Escape hatch for the one case the probe cannot serve: a practice that has
  // the pad but has not installed SigWeb yet. Without it the tab that carries
  // the setup instructions is only reachable once setup has already worked.
  const [isPadTabRevealed, setIsPadTabRevealed] = useState(false);
  useEffect(() => {
    if (!open) setIsPadTabRevealed(false);
  }, [open]);

  // The pad tab is offered when the flag is on AND this workstation has shown
  // some sign of actually having a pad: SigWeb answered now, it answered
  // recently (so a stopped service or an expired certificate still reaches the
  // fix-it state), or staff asked for it explicitly.
  const isPadTabVisible =
    isTopazEnabled &&
    (padAvailability.state === "available" ||
      padAvailability.isRemembered ||
      isPadTabRevealed);

  // Flag off => canvas, unconditionally. Reading through the flag (rather than
  // trusting the stored value) means turning the flag back off also rescues a
  // workstation that had already opted into the pad — it cannot be stranded on
  // a device screen it can no longer switch away from. The same read-through
  // covers a pad that has since gone away: no tab, no device screen.
  const effectiveMethod = isPadTabVisible ? method : "canvas";

  // Draw/Type memory for this workstation (SN-19542). Same read-through rule
  // as the Topaz flag above: a stored "type" is only honoured while the flag
  // is on AND this surface opted in, so nobody can be stranded on a desk the
  // gate no longer offers.
  const { canvasMode, setCanvasMode } = useSignatureCanvasMode();
  const isTypeEnabled =
    Boolean(flags[TYPE_TO_SIGN_FEATURE]) && Boolean(enableTypedSignature);
  const effectiveCanvasMode = isTypeEnabled ? canvasMode : "draw";

  // Which of Type / Draw / Signature pad is showing. The device wins over the
  // canvas mode: `method` says WHICH device collects the ink and `canvasMode`
  // only says how the canvas collects it, so a pad workstation is on the pad
  // tab whatever its remembered canvas mode is.
  const selectedTab = effectiveMethod === "topaz" ? "pad" : effectiveCanvasMode;

  // Nothing to choose between unless at least one alternative to Draw exists.
  // With both gates off this is false, so the desk renders exactly as it did
  // before either feature existed.
  const showModeToggle = isTypeEnabled || isPadTabVisible;

  const [cropImage, setCropImage] = useState<boolean>(true);
  const [isBusy, setIsBusy] = useState(false);

  useEffect(() => {
    setIsBusy(false);
  }, [open]);

  const defaultValues: SignaturePickerValues = useMemo(() => {
    return merge({}, DEFAULT_VALUES);
  }, []);

  const formMethods = useForm<SignaturePickerValues>({
    mode: "onChange",
    defaultValues
  });

  useEffect(() => {
    if (open) {
      setErrorMessage(null);
      setSignatureSource(null);
      setAiError(null);
    }
  }, [open]);

  useEffect(() => {
    formMethods.reset(defaultValues);
  }, [defaultValues, formMethods]);

  const handleSubmit: SubmitHandler<{
    photos: File[];
    uploadToEdocs?: boolean;
  }> = useCallback(
    async (values) => {
      setIsBusy(true);
      try {
        await onSubmit?.(values.photos, values.uploadToEdocs);
        formMethods.reset();
        onClose();
      } catch {
        // A rejected onSubmit must not strand the dialog in its loading
        // state — keep it open so the user can retry, and surface the error.
        setErrorMessage("Failed to save the signature. Please try again.");
      } finally {
        setIsBusy(false);
      }
    },
    [onSubmit, formMethods, onClose]
  );
  const handleDelete = useCallback(async () => {
    setIsBusy(true);
    try {
      await onSubmit?.([]);
      formMethods.reset();
      onClose();
    } catch {
      setErrorMessage("Failed to remove the signature. Please try again.");
    } finally {
      setIsBusy(false);
    }
  }, [onClose, onSubmit, formMethods]);

  const handleClose = useCallback(
    (event: any, reason: string) => {
      if (reason === "backdropClick") {
        return;
      }
      onClose();
      setStep("main");
    },
    [onClose]
  );

  // Shared by both desks so the canvas and the pad behave identically once a
  // PNG File exists — the only difference between providers is how it is drawn.
  const handleDeskCapture = useCallback(
    (file: File) => {
      formMethods.setValue("photos", [file]);
      // Drawn OR typed — see the signatureSource declaration.
      setSignatureSource("desk");
      if (enableHandSignatureOnly) {
        formMethods.handleSubmit(handleSubmit)();
      } else {
        setStep("main");
      }
    },
    [enableHandSignatureOnly, formMethods, handleSubmit]
  );

  const photos = formMethods.watch("photos");

  const handleEnhanceSignature = useCallback(async () => {
    let original = photos?.at(0);

    if (!original && currentImageSrc) {
      try {
        // `no-store` is load-bearing, not an optimisation. The same URL is rendered in an
        // <img> right beside this, which loads in opaque no-CORS mode; the browser can cache
        // that entry and then serve it to this CORS-mode fetch, which fails the CORS check.
        // utils/common.ts documents the mechanism (suspected, not confirmed against the asset
        // host) and #6348 worked around the same class this way. (SN-18765)
        const response = await fetch(currentImageSrc, { cache: "no-store" });
        const blob = await response.blob();
        original = new File([blob], "signature.png", {
          type: blob.type || "image/png"
        });
      } catch {
        setAiError(
          "Couldn't load the current image. You can upload a new one instead."
        );
        return;
      }
    }

    if (!original) return;
    setAiError(null);

    const formData = new FormData();
    formData.append("image", original);

    try {
      const blob = await enhanceSignature({ formData }).unwrap();
      const improved = new File([blob], "signature.png", { type: "image/png" });
      formMethods.setValue("photos", [improved], { shouldValidate: true });
    } catch (err) {
      setAiError(
        getEnhanceErrorMessage(
          err,
          "Couldn't improve the signature. You can still save the original."
        )
      );
    }
  }, [photos, currentImageSrc, enhanceSignature, formMethods]);

  useEffect(() => {
    if (!photos?.length) {
      setErrorMessage(null);
      return;
    }

    const sizeError = validatePhotoFileSizes(photos, maxSizeLimit);
    if (sizeError) {
      setErrorMessage(sizeError);
      return;
    }

    const firstPhoto = photos.at(0);
    if (!firstPhoto || !maxWidth) {
      setErrorMessage(null);
      return;
    }

    let isCancelled = false;

    (async () => {
      const dimensionError = await validatePhotoDimensions(
        firstPhoto,
        maxWidth
      );
      if (!isCancelled) {
        setErrorMessage(dimensionError);
      }
    })();

    return () => {
      isCancelled = true;
    };
  }, [maxSizeLimit, maxWidth, photos]);

  // Both desks take an identical prop contract — the only thing that differs
  // between providers is how the ink is collected, never what comes out.
  const deskProps = {
    width: largeSize ? 700 : 400,
    height: largeSize ? 314 : 200,
    submitButtonText,
    isSubmitButton: enableHandSignatureOnly,
    isLoading: isBusy || isLoading,
    beforeButtonSlot: showUploadToEdocsCheckbox ? (
      <FormControlLabel
        label="Upload to E-Docs"
        control={
          <CheckboxFormField
            name="uploadToEdocs"
            control={formMethods.control}
            testId="uploadToEdocs"
            sx={{ ml: 0 }}
          />
        }
      />
    ) : undefined,
    onCapture: handleDeskCapture
  };

  return (
    <Dialog
      fullWidth={Boolean(largeSize)}
      maxWidth={largeSize ? "lg" : undefined}
      open={open}
      onClose={handleClose}
      sx={{
        width: largeSize ? "800px" : undefined,
        margin: "0 auto"
      }}
    >
      <Box
        display="flex"
        flexDirection="column"
        alignItems="center"
        sx={{
          boxSizing: "border-box",
          width: "100%",
          py: 4,
          px: 2,

          zIndex: 1800
        }}
      >
        <IconButton
          size="small"
          aria-label="close"
          color="inherit"
          onClick={onClose}
          sx={{ position: "absolute", top: 4, right: 4 }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
        {step === "main" && (
          <>
            <Typography variant="h5" marginBottom={1} component="div">
              {title}
            </Typography>
            <Typography
              variant="body1"
              component="div"
              align={isTypeEnabled ? "center" : "inherit"}
            >
              {isTypeEnabled
                ? "To Type or Draw a Signature click the icon below"
                : "Draw a signature"}
            </Typography>
            <IconButton
              onClick={() => setStep("signature-desk")}
              size="large"
              aria-label={
                isTypeEnabled ? "Type or draw a signature" : undefined
              }
              sx={{
                backgroundColor: theme.palette.grey[100],
                mt: 2,
                p: 6,
                // Keep the button circular: with the keyboard beside the pen
                // the content is 56px wide, so trade horizontal padding for
                // content width (32 + 56 + 32 = 24 + 48 + 48 = 120).
                ...(isTypeEnabled && { px: 4 })
              }}
            >
              {/* Type leads everywhere on this surface: keyboard before pen,
                  matching the Type/Draw toggle order on the desk. */}
              {isTypeEnabled && (
                <KeyboardAltOutlinedIcon
                  sx={{ height: 24, width: 24, mr: 1 }}
                />
              )}
              <SignDocumentIcon style={{ height: "24px", width: "24px" }} />
            </IconButton>
            {/* With typed signing active, Type/Draw are the only ways to
                produce a signature (SN-19567): the upload dropzone and its
                OR go away, while ImageUpload keeps rendering the captured
                file row and the "Current Image" row with its delete action. */}
            {!isTypeEnabled && (
              <Divider
                sx={{
                  width: 150,
                  m: 2
                }}
              >
                OR
              </Divider>
            )}
            <FormProvider {...formMethods}>
              {/* Desk captures arrive already trimmed to their ink (both
                  desks crop before export), so with typed signing active —
                  where the desk is the only file source — the manual crop
                  editor is redundant UI over an auto-cropped image, and a
                  trap: dragging its box without pressing "Crop Image"
                  uploads the original. Show the plain review row instead. */}
              <ImageUpload
                name="photos"
                onRemove={() => {
                  setSignatureSource(null);
                  handleDelete();
                }}
                onChange={(files) =>
                  setSignatureSource(files.length ? "upload" : null)
                }
                hasInitialImage={hasInitialImage}
                currentImageSrc={currentImageSrc}
                cropImage={isTypeEnabled ? false : cropImage}
                setCropImage={setCropImage}
                hideUploadInput={isTypeEnabled}
                largePreview
                currentImageAlt="Current signature"
              />
            </FormProvider>
            {showUploadToEdocsCheckbox && (
              <Box mt={-1}>
                <FormControlLabel
                  label="Upload to E-Docs"
                  control={
                    <CheckboxFormField
                      name="uploadToEdocs"
                      control={formMethods.control}
                      testId="uploadToEdocs"
                      sx={{ ml: 0 }}
                    />
                  }
                />
              </Box>
            )}

            {Boolean(aiError) && (
              <Alert severity="warning" sx={{ mt: 1 }}>
                {aiError}
              </Alert>
            )}
            {isAiEnhanceEnabled &&
              ((Boolean(photos?.length) && signatureSource !== "desk") ||
                (hasInitialImage &&
                  Boolean(currentImageSrc) &&
                  !photos?.length)) && (
                <LoadingButton
                  type="button"
                  variant="outlined"
                  size="large"
                  loading={isEnhancing}
                  startIcon={<AutoFixHighIcon />}
                  onClick={handleEnhanceSignature}
                  data-testid="remove-background"
                  sx={{ mb: 1 }}
                >
                  Remove Background
                </LoadingButton>
              )}
            <LoadingButton
              variant="contained"
              size="large"
              type="submit"
              loading={isLoading}
              sx={{
                marginTop: 2
              }}
              disabled={Boolean(errorMessage)}
              onClick={formMethods.handleSubmit(handleSubmit)}
            >
              {submitButtonName}
            </LoadingButton>
          </>
        )}
        {step === "signature-desk" && (
          <Box
            display="flex"
            flexDirection="column"
            sx={{
              minHeight: "300px"
            }}
          >
            <Box display="flex" alignItems="center" marginBottom={2}>
              {!enableHandSignatureOnly && (
                <IconButton
                  sx={{ marginRight: 1 }}
                  onClick={() => setStep("main")}
                >
                  <ArrowBackIcon />
                </IconButton>
              )}

              <Typography variant="h5" component="div" fontWeight={700}>
                {signatureDeskTitle}
              </Typography>
            </Box>
            <Box>
              {/* One control for all three ways to sign. Type and Draw keep
                  their positions and their behaviour; the pad is appended,
                  never inserted, so the 52 practices signing in Draw today
                  find it exactly where they left it. */}
              {showModeToggle && (
                <ToggleButtonGroup
                  exclusive
                  fullWidth
                  size="medium"
                  color="primary"
                  aria-label="Signature entry mode"
                  data-testid="signature-canvas-mode-toggle"
                  value={selectedTab}
                  onChange={(_event, next) => {
                    if (!next) return;
                    if (next === "pad") {
                      setMethod("topaz");
                      return;
                    }
                    // Leaving the pad means leaving the DEVICE, so both halves
                    // of the remembered state move together — otherwise a
                    // workstation would come back to the pad tomorrow having
                    // deliberately switched away from it today.
                    setMethod("canvas");
                    setCanvasMode(next);
                  }}
                  sx={{ mb: 2 }}
                >
                  {/* Type first by design (CEO call on SN-19567); the
                      DEFAULT mode stays draw — order is presentation,
                      not selection. */}
                  {isTypeEnabled && (
                    <ToggleButton value="type">Type</ToggleButton>
                  )}
                  <ToggleButton value="draw">Draw</ToggleButton>
                  {isPadTabVisible && (
                    <ToggleButton value="pad" data-testid="signature-pad-tab">
                      Signature pad
                      {/* The tab appears on its own the first time SigWeb
                          answers, so the badge is what turns a silent pop-in
                          into an invitation. It retires once the workstation
                          has chosen the pad, which `method` remembers. Pinned
                          INSIDE the button's box: a badge hung off the corner
                          overflows the toggle group and gives the whole dialog
                          a horizontal scrollbar. */}
                      {method !== "topaz" && (
                        <Box
                          component="span"
                          aria-label="New"
                          sx={{
                            position: "absolute",
                            top: 3,
                            right: 4,
                            px: 0.5,
                            borderRadius: "4px",
                            bgcolor: "primary.main",
                            color: "primary.contrastText",
                            fontSize: "0.5625rem",
                            lineHeight: "14px",
                            letterSpacing: "0.04em",
                            textTransform: "uppercase"
                          }}
                        >
                          New
                        </Box>
                      )}
                    </ToggleButton>
                  )}
                </ToggleButtonGroup>
              )}

              {effectiveMethod === "topaz" ? (
                <TopazSignatureDesk
                  {...deskProps}
                  entryPoint={entryPoint}
                  onDeviceDetected={padAvailability.remember}
                />
              ) : effectiveCanvasMode === "type" ? (
                <TypeSignatureDesk {...deskProps} />
              ) : (
                <SignatureDesk {...deskProps} />
              )}

              {/* Only while the tab is hidden: once it is on screen it IS the
                  way to reach the pad, and a link saying the same thing beside
                  it just competes with it. */}
              {isTopazEnabled && !isPadTabVisible && (
                <Box sx={{ mt: 1.5, textAlign: "center" }}>
                  <Link
                    component="button"
                    // Without an explicit button type this would submit the
                    // enclosing RHF form on click instead of switching devices.
                    type="button"
                    variant="body2"
                    underline="hover"
                    data-testid="signature-method-toggle"
                    onClick={() => {
                      setIsPadTabRevealed(true);
                      setMethod("topaz");
                    }}
                  >
                    Using a Topaz signature pad? Click here
                  </Link>
                </Box>
              )}
            </Box>
          </Box>
        )}
        {Boolean(errorMessage) && (
          <Alert severity="error" sx={{ mt: 2, mb: -1 }}>
            {errorMessage}
          </Alert>
        )}
      </Box>
    </Dialog>
  );
}
